// Foundations — board section 01: color (primitive ramps, semantic roles,
// status), typography, spacing, radius, elevation. Swatches show live values.

import { useMemo } from 'react';
import Icon from '@/components/common/Icon';
import {
  PRIMITIVE_RAMPS,
  SEMANTIC_GROUPS,
  STATUS_TOKENS,
  RADIUS_TOKENS,
  SHADOW_TOKENS,
  TYPE_SPECIMENS,
  SPACING_TOKENS,
  FONT_FAMILIES,
  useResolvedTokens,
} from './tokens';

const SPECIMEN_TEXT = 'Run the day with clarity';

const FoundationsSection = () => {
  // Gather every token whose value we want to display, resolve in one pass.
  const allNames = useMemo(() => {
    const names = new Set<string>();
    PRIMITIVE_RAMPS.forEach((r) => r.steps.forEach((s) => names.add(s)));
    SEMANTIC_GROUPS.forEach((g) => g.tokens.forEach((t) => names.add(t)));
    STATUS_TOKENS.forEach((s) => {
      [s.base, s.bg, s.fg, s.border].forEach((n) => n && names.add(n));
    });
    RADIUS_TOKENS.forEach((r) => names.add(r));
    SPACING_TOKENS.forEach((s) => names.add(s));
    TYPE_SPECIMENS.forEach((t) => names.add(t.token));
    return Array.from(names);
  }, []);
  const values = useResolvedTokens(allNames);

  return (
    <section className='theme-page__section'>
      <h2 className='theme-page__section-title'>
        <Icon name='palette' size={18} /> 01 · Foundations
      </h2>

      {/* Color — primitive ramps */}
      <h3 className='theme-page__block-title'>
        Color · primitive ramps
        <span className='theme-page__note'>Tier 1 — never reference directly in product UI</span>
      </h3>
      <div className='theme-page__ramps'>
        {PRIMITIVE_RAMPS.map((ramp) => (
          <div key={ramp.title} className='theme-page__ramp'>
            <p className='theme-page__ramp-title'>{ramp.title}</p>
            <div className='theme-page__ramp-strip'>
              {ramp.steps.map((step) => (
                <div
                  key={step}
                  className='theme-page__ramp-cell'
                  title={`${step} · ${values[step] || ''}`}
                >
                  <span className='theme-page__ramp-chip' style={{ background: `var(${step})` }} />
                  <span className='theme-page__ramp-label'>{step.replace(/^--[a-z]+-?/, '')}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Color — semantic roles */}
      <h3 className='theme-page__block-title'>
        Color · semantic roles
        <span className='theme-page__note'>Tier 2 — the tokens components consume</span>
      </h3>
      <div className='theme-page__grid theme-page__grid--roles'>
        {SEMANTIC_GROUPS.map((group) => (
          <div key={group.title} className='theme-page__card'>
            <p className='theme-page__card-title'>{group.title}</p>
            {group.tokens.map((token) => (
              <div key={token} className='theme-page__swatch-row'>
                <span className='theme-page__swatch' style={{ background: `var(${token})` }} />
                <code className='theme-page__token-name'>{token}</code>
                <span className='theme-page__token-value'>{values[token]}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Color — status intents */}
      <h3 className='theme-page__block-title'>Status · color + icon (never color alone)</h3>
      <div className='theme-page__grid theme-page__grid--status'>
        {STATUS_TOKENS.map((s) => (
          <div
            key={s.name}
            className='theme-page__status'
            style={{
              background: `var(${s.bg})`,
              color: `var(${s.fg})`,
              borderColor: s.border ? `var(${s.border})` : 'transparent',
            }}
          >
            <span
              className='theme-page__status-dot'
              style={{ background: s.base ? `var(${s.base})` : `var(${s.fg})` }}
            />
            <span>{s.name}</span>
          </div>
        ))}
      </div>

      {/* Typography */}
      <h3 className='theme-page__block-title'>
        Typography
        <span className='theme-page__note'>Tier 2 — the --fs-* type scale</span>
      </h3>
      <div className='theme-page__card'>
        {TYPE_SPECIMENS.map((t) => (
          <div key={t.name} className='theme-page__specimen'>
            <span className='theme-page__specimen-meta'>
              {t.name}
              <code className='theme-page__token-name'>{t.token}</code>
              <span className='theme-page__token-value'>{values[t.token]}</span>
            </span>
            <span
              className='theme-page__specimen-text'
              style={{ fontSize: `var(${t.token})`, fontFamily: `${t.family}, sans-serif` }}
            >
              {SPECIMEN_TEXT}
            </span>
          </div>
        ))}
      </div>
      <div className='theme-page__fonts'>
        {FONT_FAMILIES.map((f) => (
          <div
            key={f.label}
            className='theme-page__font'
            style={{ fontFamily: `${f.family}, sans-serif` }}
          >
            <span className='theme-page__token-value'>{f.label}</span>
            Aa · {SPECIMEN_TEXT}
          </div>
        ))}
      </div>

      {/* Spacing */}
      <h3 className='theme-page__block-title'>
        Spacing · 4 / 8 grid
        <span className='theme-page__note'>Tier 2 — the --space-* scale</span>
      </h3>
      <div className='theme-page__card theme-page__spacing'>
        {SPACING_TOKENS.map((token) => (
          <div key={token} className='theme-page__spacing-row'>
            <code className='theme-page__token-name'>{token}</code>
            <span className='theme-page__token-value'>{values[token]}</span>
            <span className='theme-page__spacing-bar' style={{ width: `var(${token})` }} />
          </div>
        ))}
      </div>

      {/* Radius */}
      <h3 className='theme-page__block-title'>Radius</h3>
      <div className='theme-page__tiles'>
        {RADIUS_TOKENS.map((token) => (
          <div key={token} className='theme-page__tile'>
            <span className='theme-page__tile-box' style={{ borderRadius: `var(${token})` }} />
            <code className='theme-page__token-name'>{token}</code>
            <span className='theme-page__token-value'>{values[token]}</span>
          </div>
        ))}
      </div>

      {/* Elevation */}
      <h3 className='theme-page__block-title'>Elevation</h3>
      <div className='theme-page__tiles'>
        {SHADOW_TOKENS.map((token) => (
          <div key={token} className='theme-page__tile'>
            <span
              className='theme-page__tile-box theme-page__tile-box--elevated'
              style={{ boxShadow: `var(${token})` }}
            />
            <code className='theme-page__token-name'>{token}</code>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FoundationsSection;
